import { useCallback, useState } from 'react';
import type { ProvidedJobPost } from './useProvidedJobPosts';
import type { SearchQuery } from './useProvidedJobPosts';
import usePdf from './usePdf';
import ShareContentDialog from '../components/ShareContentDialog';
import SuggestCandidatesModal from '../components/SuggestCandidatesModal';
import useBulkSuggestJobPosts from '../api/useBulkSuggestJobPosts';
import AddToListDialog from '../../provided_job_post_lists/components/AddToListDialog';
import fetchAllSelectedJobPosts, {
  BulkActionLimitExceededError,
} from '../api/fetchAllSelectedJobPosts';
import { BULK_ACTION_MAX_COUNT } from '../constant';
import { isAllMode, selectionCount, type Selection } from './useSelection';

const LIMIT_EXCEEDED_MESSAGE = `一度に処理できるのは${BULK_ACTION_MAX_COUNT}件までです。検索条件を絞り込んでから再度お試しください。`;

type Params = {
  providedJobPosts: ProvidedJobPost[];
  selection: Selection;
  searchQuery: SearchQuery;
};

export default function useBulkShareAndSuggest({
  providedJobPosts,
  selection,
  searchQuery,
}: Params) {
  const { downloadMultiplePdfs } = usePdf();
  const [isWebShareDialogOpen, setIsWebShareDialogOpen] = useState(false);
  const [webShareText, setWebShareText] = useState('');
  const [isSuggestModalOpen, setIsSuggestModalOpen] = useState(false);
  const [suggestJobPosts, setSuggestJobPosts] = useState<
    Array<{
      id: number;
      companyName: string;
      position: string;
      location: string;
    }>
  >([]);
  const { mutateAsync: bulkSuggestJobPosts } = useBulkSuggestJobPosts();
  const [isAddToListDialogOpen, setIsAddToListDialogOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [pdfProgress, setPdfProgress] = useState<{ current: number; total: number } | null>(null);

  /** 選択状態から対象の求人レコードを解決する（manual/all 共通）。対象なし・上限超過・取得失敗時は null */
  const resolveTargets = useCallback(async (): Promise<ProvidedJobPost[] | null> => {
    const count = selectionCount(selection);
    if (count === 0) return null;

    // 上限チェック（all モードは selection 保持時点の totalCount で事前判定。最新件数は fetch 側でも再判定）
    if (count > BULK_ACTION_MAX_COUNT) {
      alert(LIMIT_EXCEEDED_MESSAGE);
      return null;
    }

    if (!isAllMode(selection)) {
      // 個別選択モード: 読み込み済みレコードから抽出
      return providedJobPosts.filter((post) => selection.ids.has(post.id));
    }

    // 全件選択モード: index API を全ページ取得して対象を解決
    try {
      return await fetchAllSelectedJobPosts({
        searchQuery,
        excludedIds: selection.excludedIds,
      });
    } catch (error) {
      if (error instanceof BulkActionLimitExceededError) {
        alert(LIMIT_EXCEEDED_MESSAGE);
      } else {
        console.error('対象求人の取得エラー:', error);
        alert('求人の取得に失敗しました。時間をおいて再度お試しください。');
      }
      return null;
    }
  }, [selection, providedJobPosts, searchQuery]);

  /** PDF共有用: 対象の求人IDを解決する。manual モードは読み込み済みレコードに依存せず selection.ids をそのまま使う */
  const resolveTargetIds = useCallback(async (): Promise<number[] | null> => {
    if (!isAllMode(selection)) {
      const count = selectionCount(selection);
      if (count === 0) return null;
      if (count > BULK_ACTION_MAX_COUNT) {
        alert(LIMIT_EXCEEDED_MESSAGE);
        return null;
      }
      return Array.from(selection.ids);
    }
    const targets = await resolveTargets();
    return targets ? targets.map((post) => post.id) : null;
  }, [selection, resolveTargets]);

  const handleAddToList = useCallback(() => {
    if (selectionCount(selection) === 0) return;
    setIsAddToListDialogOpen(true);
  }, [selection]);

  const handleWebShare = useCallback(async () => {
    if (isProcessing) return;
    setIsProcessing(true);
    try {
      const targets = await resolveTargets();
      if (!targets) return;

      // 選択中の求人票のうち、公開中のものだけを対象にする
      const availablePosts = targets.filter((post) => post.is_available);

      if (availablePosts.length === 0) {
        alert('公開中の求人票が選択されていません。公開中の求人票のみ共有できます。');
        return;
      }

      // 共有用テキストを生成（1求人ごとのWEB共有と同じ形式）
      const text = availablePosts
        .map((post) => {
          const companyName = post.company?.name ?? '';
          const title = post.title ?? '';
          const openUrl = post.urls.open ?? '';
          return [companyName, title, openUrl].filter((v) => v).join('\n');
        })
        .filter((v) => v)
        .join('\n\n');

      if (!text) {
        alert('共有可能なコンテンツがありません');
        return;
      }

      setWebShareText(text);
      setIsWebShareDialogOpen(true);
    } finally {
      setIsProcessing(false);
    }
  }, [isProcessing, resolveTargets]);

  const handlePdfShare = useCallback(async () => {
    if (isProcessing) return;
    setIsProcessing(true);
    try {
      const targetIds = await resolveTargetIds();
      if (!targetIds) return;

      // 1件目の完了を待たずに作成中表示を出す
      setPdfProgress({ current: 0, total: targetIds.length });
      const result = await downloadMultiplePdfs(targetIds, {
        onProgress: (current, total) => setPdfProgress({ current, total }),
      });
      if (result.missingCount > 0) {
        alert(
          `${result.downloadedCount}件の求人票をダウンロードしました。${result.missingCount}件は削除済みなどの理由で取得できませんでした。`
        );
      }
    } catch (error) {
      console.error('PDFダウンロードエラー:', error);
      alert('PDFのダウンロードに失敗しました。もう一度お試しください。');
    } finally {
      setPdfProgress(null);
      setIsProcessing(false);
    }
  }, [isProcessing, resolveTargetIds, downloadMultiplePdfs]);

  const handleSuggestToCandidates = useCallback(async () => {
    if (isProcessing) return;
    setIsProcessing(true);
    try {
      const targets = await resolveTargets();
      if (!targets || targets.length === 0) return;

      setSuggestJobPosts(
        targets.map((post) => ({
          id: post.id,
          companyName: post.company?.name ?? '',
          position: post.job_position_name ?? '',
          location: post.working_location ?? '',
        }))
      );
      setIsSuggestModalOpen(true);
    } finally {
      setIsProcessing(false);
    }
  }, [isProcessing, resolveTargets]);

  const dialogs = (
    <>
      <ShareContentDialog
        title="求人票のURLをコピーする"
        shareText={webShareText}
        open={isWebShareDialogOpen}
        onOpenChange={setIsWebShareDialogOpen}
      />
      {suggestJobPosts.length > 0 && (
        <SuggestCandidatesModal
          isOpen={isSuggestModalOpen}
          onClose={() => {
            setIsSuggestModalOpen(false);
            setSuggestJobPosts([]);
          }}
          jobPosts={suggestJobPosts}
          onSubmit={async (candidateIds) => {
            await bulkSuggestJobPosts({
              jobPostIds: suggestJobPosts.map((post) => post.id),
              candidateProfileIds: candidateIds,
            });
          }}
          onSuccess={() => {
            setIsSuggestModalOpen(false);
            setSuggestJobPosts([]);
          }}
        />
      )}
      <AddToListDialog
        open={isAddToListDialogOpen}
        onOpenChange={setIsAddToListDialogOpen}
        selection={selection}
        searchQuery={searchQuery}
        onCompleted={() => {
          setIsAddToListDialogOpen(false);
        }}
      />
    </>
  );

  return {
    handleWebShare,
    handlePdfShare,
    handleSuggestToCandidates,
    handleAddToList,
    isProcessing,
    pdfProgress,
    dialogs,
  };
}
